import type { StyleSpecification } from 'maplibre-gl';
import { baseMaps } from './const/baseMaps';

export type CatalogStyleAuth = {
  styleUrl: string;
  apikey: string;
  origin: string;
};

const SOURCE_REF_RE =
  /^geobox:\/\/(view|vector|tileset|raster|mosaic|hillshade)\/([^/?#]+)/;
const SOURCE_LAYER_RE = /^(view|vector)\/(.+)$/;

type TileSourceSpec = {
  type?: string;
  url?: string;
  tiles?: string[];
  minzoom?: number;
  maxzoom?: number;
  bounds?: [number, number, number, number];
  scheme?: string;
};

type StyleLayerRef = {
  source?: string;
  'source-layer'?: string;
  'source-layer-id'?: string | number;
};

/** Append `apikey` as the last query parameter without rewriting `{z}/{x}/{y}` placeholders. */
export function appendApikey(url: string, apikey: string): string {
  if (!apikey || !url || url.includes('apikey=')) return url;
  const join = url.includes('?') ? '&' : '?';
  return `${url}${join}apikey=${encodeURIComponent(apikey)}`;
}

/** Public style URL + apikey for catalog basemaps rasterized from `/v1/maps/{uuid}/tiles`. */
export function catalogStyleAuthForMapUuid(uuid: string): CatalogStyleAuth | null {
  const id = String(uuid ?? '').trim();
  if (!id) return null;

  for (const item of Object.values(baseMaps) as Array<{ url?: unknown; proxyUrl?: unknown }>) {
    const candidates = [
      ...(Array.isArray(item.url) ? item.url : [item.url]),
      item.proxyUrl,
    ];
    for (const raw of candidates) {
      if (typeof raw !== 'string' || !raw.includes(`/v1/maps/${id}`)) continue;
      try {
        const parsed = new URL(raw.replace(/\{[xyzXYZ]+\}/g, '0'));
        const apikey = parsed.searchParams.get('apikey') || '';
        const styleUrl = appendApikey(`${parsed.origin}/v1/maps/${id}/style/`, apikey);
        return { styleUrl, apikey, origin: parsed.origin };
      } catch {
        continue;
      }
    }
  }
  return null;
}

function resourceUrl(
  origin: string,
  path: string,
  params: Array<[string, string]>,
  apikey: string,
): string | null {
  if (!origin || !apikey) return null;
  const url = new URL(path, origin.endsWith('/') ? origin : `${origin}/`);
  for (const [key, value] of params) {
    url.searchParams.set(key, value);
  }
  url.searchParams.set('apikey', apikey);
  return url.href;
}

function collectResourceIds(style: StyleSpecification) {
  const tilesets = new Set<string>();
  const vectors = new Set<string>();
  const views = new Set<string>();

  const rememberSource = (sourceId: string) => {
    const match = sourceId.match(SOURCE_REF_RE);
    if (!match) return;
    const [, kind, id] = match;
    if (kind === 'tileset') tilesets.add(id);
    else if (kind === 'vector') vectors.add(id);
    else if (kind === 'view') views.add(id);
  };

  for (const sourceId of Object.keys(style.sources ?? {})) {
    rememberSource(sourceId);
  }

  for (const layer of (style.layers ?? []) as StyleLayerRef[]) {
    if (layer.source) rememberSource(String(layer.source));
    const sourceLayer = String(layer['source-layer'] ?? '');
    const layerMatch = sourceLayer.match(SOURCE_LAYER_RE);
    const layerId = layer['source-layer-id'];
    if (!layerMatch || layerId == null || layerId === '') continue;
    const bucket = layerMatch[1] === 'view' ? views : vectors;
    bucket.add(String(layerId));
  }

  return { tilesets, vectors, views };
}

async function fetchJson(url: string | null): Promise<unknown> {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function indexUuidById(rows: unknown): Map<string, string> {
  const map = new Map<string, string>();
  if (!Array.isArray(rows)) return map;
  for (const row of rows) {
    const record = asRecord(row);
    const id = record?.id;
    const uuid = record?.uuid;
    if ((typeof id === 'number' || typeof id === 'string') && typeof uuid === 'string' && uuid) {
      map.set(String(id), uuid);
    }
  }
  return map;
}

function tilejsonPath(kind: string, uuid: string): string | null {
  if (kind === 'tileset') return `/v1/tilesets/${uuid}/tilejson.json`;
  if (kind === 'vector') return `/v1/vectorLayers/${uuid}/tilejson.json`;
  if (kind === 'view') return `/v1/vectorLayerViews/${uuid}/tilejson.json`;
  return null;
}

function isTilejsonUrl(url: string): boolean {
  return /tilejson\.json(\?|$)/i.test(url);
}

async function inlineAuthedTiles(source: TileSourceSpec, apikey: string): Promise<void> {
  if (typeof source.url === 'string') {
    source.url = appendApikey(source.url, apikey);
  }
  if (Array.isArray(source.tiles)) {
    source.tiles = source.tiles.map((tile) =>
      typeof tile === 'string' ? appendApikey(tile, apikey) : tile,
    );
  }
  if (typeof source.url !== 'string' || !isTilejsonUrl(source.url)) return;

  const json = asRecord(await fetchJson(source.url));
  const tiles = Array.isArray(json?.tiles)
    ? json.tiles.filter((tile): tile is string => typeof tile === 'string')
    : [];
  if (!tiles.length) return;

  source.tiles = tiles.map((tile) => appendApikey(tile, apikey));
  if (typeof json?.minzoom === 'number') source.minzoom = json.minzoom;
  if (typeof json?.maxzoom === 'number') source.maxzoom = json.maxzoom;
  if (
    Array.isArray(json?.bounds)
    && json.bounds.length === 4
    && json.bounds.every((value) => typeof value === 'number')
  ) {
    source.bounds = json.bounds as [number, number, number, number];
  }
  delete source.url;
}

/**
 * Authorize a PBF basemap style with the catalog apikey.
 * Fetches the map's tilesets and vector/view layers with `apikey` at the end of
 * each URL, then rewrites tilejson/tile templates the same way.
 */
export async function prepareVectorBasemapStyle(
  style: StyleSpecification,
  apikey: string,
  origin: string,
): Promise<StyleSpecification> {
  if (!apikey) return style;

  const { tilesets, vectors, views } = collectResourceIds(style);
  const sources = Object.entries(style.sources ?? {}) as Array<[string, TileSourceSpec]>;
  const sourcesMissingUrl = new Set(
    sources
      .filter(([, source]) => !source || typeof source.url !== 'string')
      .map(([sourceId]) => sourceId),
  );
  const inlineExisting = Promise.all(
    sources
      .filter(([, source]) => source && typeof source.url === 'string')
      .map(([, source]) => inlineAuthedTiles(source, apikey)),
  );

  const [tilesetRows, vectorRows, viewRows] = await Promise.all([
    fetchJson(tilesets.size
      ? resourceUrl(origin, '/v1/tilesets/get-tilesets/', [['ids', [...tilesets].join(',')]], apikey)
      : null),
    fetchJson(vectors.size
      ? resourceUrl(
        origin,
        '/v1/vectorLayers/get-layers/',
        [['ids', [...vectors].join(',')], ['include_settings', 'true']],
        apikey,
      )
      : null),
    fetchJson(views.size
      ? resourceUrl(
        origin,
        '/v1/vectorLayerViews/get-layers/',
        [['ids', [...views].join(',')], ['include_settings', 'true']],
        apikey,
      )
      : null),
  ]);

  const uuidByKind: Record<string, Map<string, string>> = {
    tileset: indexUuidById(tilesetRows),
    vector: indexUuidById(vectorRows),
    view: indexUuidById(viewRows),
  };

  await inlineExisting;

  for (const [sourceId, source] of sources) {
    if (!sourcesMissingUrl.has(sourceId)) continue;
    if (!source || typeof source !== 'object' || typeof source.url === 'string') continue;
    const match = sourceId.match(SOURCE_REF_RE);
    if (!match) continue;
    const uuid = uuidByKind[match[1]]?.get(match[2]);
    const path = uuid ? tilejsonPath(match[1], uuid) : null;
    if (!path || !origin) continue;
    source.url = appendApikey(`${origin}${path}`, apikey);
  }

  await Promise.all(
    sources
      .filter(([, source]) => source && typeof source.url === 'string')
      .map(([, source]) => inlineAuthedTiles(source, apikey)),
  );

  return style;
}
